import * as Sentry from "@sentry/react";

import type {CameraPermissionState} from "@/src/modules/core/lib/camera-permission";

import type {CameraBlockDiagnosis} from "@/src/modules/core/lib/camera-permission";

import type {CameraEnvironmentDiagnostics} from "@/src/modules/core/lib/camera-environment";

import {

  getGeolocationPlatform,

  isStandalonePwa,

} from "@/src/modules/core/lib/geolocation-platform";

import type {FilePickerPendingSource} from "@/src/modules/core/lib/file-picker-pending";



import {captureDeviceFailure} from "./device-capture";
import {DEVICE_FAILURE_ERROR_CODES} from "./failure-domains";
import {flushGlitchtipEvents} from "./capture";



export type CameraTelemetryInput = {

  feature: string;

  operation?: string;

};



export type CameraGestureSource =
  | "pointerdown"
  | "click"
  | "permission_dialog_retry";

export type PhotoSource = "in_app_camera" | "gallery";

export type PhotoCaptureMethod = "image_capture" | "video_frame";



type CameraTelemetryExtra = Record<string, string | number | undefined>;



function addCameraBreadcrumb(

  message: string,

  data?: Record<string, string | number | boolean | undefined>,

): void {

  Sentry.addBreadcrumb({

    category: "camera",

    message,

    level: "info",

    data,

  });

}



function getCameraPageContext(): CameraTelemetryExtra {

  if (typeof window === "undefined") {

    return {};

  }



  const hashRoute = window.location.hash.replace(/^#/, "");

  const route = hashRoute || window.location.pathname || "/";



  return {

    page_origin: window.location.origin,

    page_route: route,

  };

}



function getCameraTelemetryContext(extra?: CameraTelemetryExtra): CameraTelemetryExtra {

  return {

    platform: getGeolocationPlatform(),

    standalone_pwa: isStandalonePwa() ? 1 : 0,

    ...getCameraPageContext(),

    ...extra,

  };

}



function diagnosisExtra(diagnosis?: CameraBlockDiagnosis): CameraTelemetryExtra {

  return diagnosis ? {diagnosis_hint: diagnosis} : {};

}



function emitCameraFailure(

  input: Parameters<typeof captureDeviceFailure>[0],

): void {

  captureDeviceFailure(input);

  void flushGlitchtipEvents();

}



export function captureCameraPermissionDeniedTelemetry(

  input: CameraTelemetryInput & {

    permissionState?: CameraPermissionState;

    mediaErrorName?: string;

    environment?: CameraEnvironmentDiagnostics;

    gestureSource?: CameraGestureSource;
    diagnosis?: CameraBlockDiagnosis;
  },
): void {

  emitCameraFailure({

    feature: input.feature,

    errorCode: DEVICE_FAILURE_ERROR_CODES.CAMERA_PERMISSION_DENIED,

    operation: input.operation,

    level: "warning",

    extra: getCameraTelemetryContext({

      permission_state: input.permissionState,

      media_error_name: input.mediaErrorName,

      gesture_source: input.gestureSource,

      permissions_policy_reported_blocked:

        input.environment && !input.environment.permissionsPolicyAllowsCamera ? 1 : 0,

      ...diagnosisExtra(input.diagnosis),
    }),

  });

  addCameraBreadcrumb("camera:permission_denied", {

    feature: input.feature,

    diagnosis_hint: input.diagnosis,

    permission_state: input.permissionState,

    media_error_name: input.mediaErrorName,

  });

}



export function captureCameraUnavailableTelemetry(

  input: CameraTelemetryInput & {

    mediaErrorName?: string;

    reason?: string;

    environment?: CameraEnvironmentDiagnostics;

    gestureSource?: CameraGestureSource;

    diagnosis?: CameraBlockDiagnosis;

  },

): void {

  emitCameraFailure({

    feature: input.feature,

    errorCode: DEVICE_FAILURE_ERROR_CODES.CAMERA_UNAVAILABLE,

    operation: input.operation,

    level: "warning",

    extra: getCameraTelemetryContext({

      media_error_name: input.mediaErrorName,

      unavailable_reason: input.reason,

      gesture_source: input.gestureSource,

      permissions_policy_reported_blocked:

        input.environment && !input.environment.permissionsPolicyAllowsCamera ? 1 : 0,

      ...diagnosisExtra(input.diagnosis),

    }),

  });

  addCameraBreadcrumb("camera:unavailable", {

    feature: input.feature,

    media_error_name: input.mediaErrorName,

    unavailable_reason: input.reason,

    diagnosis_hint: input.diagnosis,

  });

}



/** Happy-path telemetry — breadcrumbs only; no GlitchTip envelope. */
export function capturePhotoAttachedTelemetry(
  input: CameraTelemetryInput & {
    photoSource: PhotoSource;
    captureMethod?: PhotoCaptureMethod;
    sourceWidth?: number;
    sourceHeight?: number;
    outputBytes?: number;
    recompressed?: boolean;
  },
): void {
  addCameraBreadcrumb("camera:photo_attached", {
    feature: input.feature,
    photo_source: input.photoSource,
    capture_method: input.captureMethod,
    source_width: input.sourceWidth,
    source_height: input.sourceHeight,
    output_bytes: input.outputBytes,
  });
}

export function captureCameraCaptureFailedTelemetry(input: CameraTelemetryInput): void {

  emitCameraFailure({

    feature: input.feature,

    errorCode: DEVICE_FAILURE_ERROR_CODES.CAMERA_CAPTURE_FAILED,

    operation: input.operation,

    level: "warning",

    extra: getCameraTelemetryContext(),

  });

}

/** Happy-path telemetry — breadcrumbs only. */
export function captureCameraFlashProbeTelemetry(
  input: CameraTelemetryInput & {
    facingMode: "environment" | "user";
    torchProbed: boolean;
    flashOnCaptureProbed: boolean;
    showControl: boolean;
    optimisticUi: boolean;
    probeAttempts: number;
  },
): void {
  addCameraBreadcrumb("camera:flash_probed", {
    feature: input.feature,
    facing_mode: input.facingMode,
    torch_probed: input.torchProbed,
    flash_on_capture_probed: input.flashOnCaptureProbed,
    flash_show_control: input.showControl,
    flash_optimistic_ui: input.optimisticUi,
    probe_attempts: input.probeAttempts,
  });
}

/** Happy-path telemetry — breadcrumbs only. */
export function captureCameraFlashToggleTelemetry(
  input: CameraTelemetryInput & {
    facingMode: "environment" | "user";
    enabled: boolean;
    torchApplied: boolean;
    optimisticUi: boolean;
    hasCaptureFlash: boolean;
  },
): void {
  addCameraBreadcrumb("camera:flash_toggled", {
    feature: input.feature,
    facing_mode: input.facingMode,
    flash_enabled: input.enabled,
    torch_applied: input.torchApplied,
    flash_optimistic_ui: input.optimisticUi,
    flash_on_capture_available: input.hasCaptureFlash,
  });
}

/** Warning — torch could not be enabled and capture-time flash is unavailable. */
export function captureCameraFlashEnableFailedTelemetry(
  input: CameraTelemetryInput & {
    facingMode: "environment" | "user";
    optimisticUi: boolean;
  },
): void {
  emitCameraFailure({
    feature: input.feature,
    errorCode: DEVICE_FAILURE_ERROR_CODES.CAMERA_FLASH_TORCH_FAILED,
    operation: input.operation,
    level: "warning",
    extra: getCameraTelemetryContext({
      facing_mode: input.facingMode,
      flash_optimistic_ui: input.optimisticUi ? 1 : 0,
    }),
  });

  addCameraBreadcrumb("camera:flash_enable_failed", {
    feature: input.feature,
    facing_mode: input.facingMode,
    flash_optimistic_ui: input.optimisticUi,
  });
}

export function captureCameraCaptureSuccessTelemetry(
  input: CameraTelemetryInput & {
    method: PhotoCaptureMethod;
    sourceWidth?: number;
    sourceHeight?: number;
    outputBytes?: number;
    flashEnabled?: boolean;
    fillLightMode?: string;
    torchProbed?: boolean;
    flashOnCaptureProbed?: boolean;
    flashOptimisticUi?: boolean;
  },
): void {
  capturePhotoAttachedTelemetry({
    feature: input.feature,
    operation: input.operation,
    photoSource: "in_app_camera",
    captureMethod: input.method,
    sourceWidth: input.sourceWidth,
    sourceHeight: input.sourceHeight,
    outputBytes: input.outputBytes,
    recompressed: false,
  });

  if (
    input.flashEnabled !== undefined ||
    input.fillLightMode !== undefined ||
    input.torchProbed !== undefined
  ) {
    addCameraBreadcrumb("camera:capture_with_flash", {
      feature: input.feature,
      flash_enabled: input.flashEnabled,
      fill_light_mode: input.fillLightMode,
      torch_probed: input.torchProbed,
      flash_on_capture_probed: input.flashOnCaptureProbed,
      flash_optimistic_ui: input.flashOptimisticUi,
    });
  }
}



export function captureFilePickCancelledTelemetry(
  input: CameraTelemetryInput & {pickerSource: FilePickerPendingSource},
): void {
  addCameraBreadcrumb("camera:file_pick_cancelled", {
    feature: input.feature,
    picker_source: input.pickerSource,
  });
}



export function capturePwaKilledDuringFilePickerTelemetry(

  input: CameraTelemetryInput & {pickerSource: FilePickerPendingSource},

): void {

  captureDeviceFailure({

    feature: input.feature,

    errorCode: DEVICE_FAILURE_ERROR_CODES.PWA_KILLED_DURING_FILE_PICKER,

    operation: input.operation,

    level: "warning",

    extra: getCameraTelemetryContext({picker_source: input.pickerSource}),

  });

}


